import axios from 'axios'

const state = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null
}

const mutations = {
  SET_USER(state, user) {
    state.user = user
    state.isAuthenticated = !!user
  },
  SET_TOKEN(state, token) {
    state.token = token
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_USER(state) {
    state.user = null
    state.token = null
    state.isAuthenticated = false
  }
}

const actions = {
  async loadUserProfile({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true)
      
      // Try to get user from Flutter bridge first
      if (window.flutterBridge && window.flutterBridge.isFlutter) {
        const user = await window.flutterBridge.getUserProfile()
        if (user) {
          commit('SET_USER', user)
          return
        }
      }
      
      // Fallback to API call
      const token = await dispatch('getAuthToken')
      if (token) {
        const response = await axios.get('/api/user/profile', {
          headers: { Authorization: `Bearer ${token}` }
        })
        commit('SET_USER', response.data)
      }
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Failed to load user profile:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async getAuthToken({ commit }) {
    try {
      if (window.flutterBridge && window.flutterBridge.isFlutter) {
        const token = await window.flutterBridge.getAuthToken()
        if (token) {
          commit('SET_TOKEN', token)
          return token
        }
      }
      
      // Fallback to localStorage
      const token = localStorage.getItem('auth_token')
      if (token) {
        commit('SET_TOKEN', token)
        return token
      }
      
      return null
    } catch (error) {
      console.error('Failed to get auth token:', error)
      return null
    }
  },
  
  async login({ commit, dispatch }, credentials) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const response = await axios.post('/api/auth/login', credentials)
      const { token, user } = response.data
      
      commit('SET_TOKEN', token)
      commit('SET_USER', user)
      
      // Store token in Flutter or localStorage
      if (window.flutterBridge && window.flutterBridge.isFlutter) {
        await window.flutterBridge.setStorageValue('auth_token', token)
      } else {
        localStorage.setItem('auth_token', token)
      }
      
      dispatch('showSuccess', 'Login successful')
      return true
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed'
      commit('SET_ERROR', message)
      dispatch('showError', message)
      return false
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async logout({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true)
      
      // Call logout API
      const token = await dispatch('getAuthToken')
      if (token) {
        await axios.post('/api/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        })
      }
      
      // Clear local data
      commit('CLEAR_USER')
      
      if (window.flutterBridge && window.flutterBridge.isFlutter) {
        await window.flutterBridge.clearStorage()
        await window.flutterBridge.logout()
      } else {
        localStorage.removeItem('auth_token')
      }
      
      dispatch('showSuccess', 'Logged out successfully')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async updateProfile({ commit, dispatch }, profileData) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const token = await dispatch('getAuthToken')
      const response = await axios.put('/api/user/profile/update', profileData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      commit('SET_USER', response.data)
      dispatch('showSuccess', 'Profile updated successfully')
      return true
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update profile'
      commit('SET_ERROR', message)
      dispatch('showError', message)
      return false
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async changePassword({ commit, dispatch }, passwordData) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const token = await dispatch('getAuthToken')
      await axios.post('/api/user/change-password', passwordData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      dispatch('showSuccess', 'Password changed successfully')
      return true
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to change password'
      commit('SET_ERROR', message)
      dispatch('showError', message)
      return false
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async forgotPassword({ commit, dispatch }, email) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      await axios.post('/api/auth/forgot-password', { email })
      
      dispatch('showSuccess', 'Password reset email sent')
      return true
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send reset email'
      commit('SET_ERROR', message)
      dispatch('showError', message)
      return false
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async resetPassword({ commit, dispatch }, resetData) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      await axios.post('/api/auth/reset-password', resetData)
      
      dispatch('showSuccess', 'Password reset successfully')
      return true
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to reset password'
      commit('SET_ERROR', message)
      dispatch('showError', message)
      return false
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async verifyOtp({ commit, dispatch }, otpData) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      await axios.post('/api/auth/verify-otp', otpData)
      
      dispatch('showSuccess', 'OTP verified successfully')
      return true
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to verify OTP'
      commit('SET_ERROR', message)
      dispatch('showError', message)
      return false
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const getters = {
  user: state => state.user,
  isAuthenticated: state => state.isAuthenticated,
  isLoading: state => state.loading,
  error: state => state.error,
  token: state => state.token,
  hasPermission: state => permission => {
    if (!state.user || !state.user.permissions) return false
    return state.user.permissions[permission] === true
  },
  hasRole: state => role => {
    if (!state.user || !state.user.roles) return false
    return state.user.roles.includes(role)
  },
  isManager: state => {
    if (!state.user || !state.user.roles) return false
    return state.user.roles.some(role => ['Manager', 'Supervisor'].includes(role))
  },
  isHR: state => {
    if (!state.user || !state.user.roles) return false
    return state.user.roles.some(role => ['HR', 'Human Resources'].includes(role))
  },
  isAdmin: state => {
    if (!state.user || !state.user.roles) return false
    return state.user.roles.some(role => ['Admin', 'Administrator'].includes(role))
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 